import { type Authentication, type AuthenticationConverter, AuthenticationError } from '../types.ts';
import { invalidRequest, invalidToken, type OAuth2Error } from './token-error.ts';
import type { ReadonlyHttpHeaders } from '../../../../types/web/http';
import type { ServerHttpRequest, ServerWebExchange } from '../../../../types/web/server';

const ACCESS_TOKEN_PARAMETER_NAME = 'access_token';
const authorizationPattern = /^Bearer\s+(?<token>[a-zA-Z0-9-._~+/]+=*)$/i;

export class Oauth2AuthenticationError extends AuthenticationError {
    readonly error: OAuth2Error;

    constructor(error: string | OAuth2Error, message?: string, options?: ErrorOptions) {
        super(message ?? (typeof error === 'string' ? undefined : error.description), options);
        this.error = typeof error === 'string' ? {errorCode: error} : error;
    }
}

export type BearerTokenAuthenticationToken = Authentication & {
    type: 'BearerToken';
    token: string;
}

export const isBearerTokenAuthenticationToken = (authentication: Authentication): authentication is BearerTokenAuthenticationToken => {
    return authentication.type === 'BearerToken';
}

const serverBearerTokenAuthenticationConverter = (opts?: {headerName?: string, uriQueryParameter?: boolean, formEncodedBodyParameter?: boolean }): AuthenticationConverter => {
    return async (exchange: ServerWebExchange) => {
        const {request} = exchange;
        return Promise.all<string[] | undefined>([
            resolveFromAuthorizationHeader(request.headers, opts?.headerName).then((token?: string) => token !== undefined ? [token] : undefined),
            resolveFromQueryString(request, opts?.uriQueryParameter),
            resolveFromBody(exchange, opts?.formEncodedBodyParameter)
        ])
            .then(rs => rs.filter(r => r !== undefined).flat(1))
            .then(resolveToken)
            .then(token => {
                if (token) return {authenticated: false, type: 'BearerToken', token};
            });
    }
}

async function resolveToken(accessTokens: string[]) {
    if (accessTokens.length === 0) {
        return;
    }
    if (accessTokens.length > 1) {
        const error = invalidRequest('Found multiple access tokens in the request');
        throw new Oauth2AuthenticationError(error);
    }

    const accessToken = accessTokens[0];
    if (!accessToken || accessToken.length === 0) {
        const error = invalidRequest('The requested access token parameter is an empty string');
        throw new Oauth2AuthenticationError(error);
    }
    return accessToken;
}

async function resolveFromAuthorizationHeader(headers: ReadonlyHttpHeaders, headerName: string = 'authorization'): Promise<string | undefined> {
    const authorization = headers.one(headerName);
    if (!authorization || !(/bearer/i).test(authorization.substring(0,))) {
        return;
    }
    const match = authorizationPattern.exec(authorization);
    if (match === null) {
        const error = invalidToken('Bearer token is malformed');
        throw new Oauth2AuthenticationError(error);
    }
    return match.groups?.token;
}

async function resolveTokens(parameters: URLSearchParams): Promise<string[] | undefined> {
    const accessTokens = parameters.getAll(ACCESS_TOKEN_PARAMETER_NAME);
    if (accessTokens.length === 0) {
        return;
    }
    return accessTokens;
}

async function resolveFromQueryString(request: ServerHttpRequest, allow = false): Promise<string[] | undefined> {
    if (!allow || request.method !== 'GET') {
        return;
    }
    return resolveTokens(request.URL.searchParams);
}

async function resolveFromBody(exchange: ServerWebExchange, allow = false): Promise<string[] | undefined> {
    const {request} = exchange;
    if (!allow
        || ('application/x-www-form-urlencoded' !== request.headers.one('content-type'))
        || request.method !== 'POST') {
        return;
    }
    const parameters = await exchange.request.formData();
    if (parameters) {
        return resolveTokens(parameters);
    }
}

export default serverBearerTokenAuthenticationConverter;
